import React from 'react';
import StatusBadge, { BadgePreset } from './StatusBadge';

// ── ItemRow ──────────────────────────────────────────────────────────────────

export interface ItemRowProps {
   /** Unique key for React */
   id: string | number;
   /** Leading icon/emoji, rendered before the label */
   icon?: React.ReactNode;
   /** Primary label text */
   label: string;
   /** Optional StatusBadge preset shown after the label */
   badge?: BadgePreset | string;
   /** Optional secondary line below the label */
   sub?: React.ReactNode;
   /** Right-side content (string or custom node) */
   value?: React.ReactNode;
   /** Tailwind color class for the value */
   valueColor?: string;
   /** Left accent bar color (CSS color string, e.g. '#34d399') */
   accentColor?: string;
   /** Elevate the row style: 'flat' = light hover only, 'card' = bordered bg */
   variant?: 'flat' | 'card';
   onClick?: (e: React.MouseEvent) => void;
}

export const ItemRow: React.FC<ItemRowProps> = ({
      id,
      icon,
      label,
      badge,
      sub,
      value,
      valueColor = 'text-gray-500',
      accentColor,
      variant = 'flat',
      onClick,
   }) => {
   const base =
      variant === 'card'
         ? 'flex items-center gap-3 bg-gray-800 border border-gray-700 hover:border-gray-500 rounded-xl px-3 py-2.5 transition-colors'
         : 'flex items-center justify-between hover:bg-gray-700 rounded-lg px-2.5 py-1.5 transition-colors';
   
   const Tag = onClick ? 'button' : 'div';
   
   return (
      <Tag
         key={id}
         onClick={onClick ? (e: React.MouseEvent) => { e.stopPropagation(); onClick(e); } : undefined}
         className={`${base} w-full text-left ${onClick ? 'cursor-pointer' : ''}`}
      >
         {/* Left accent bar (card variant only) */}
         {accentColor && variant === 'card' && (
            <div className="w-1 self-stretch rounded-full bg-gray-700 shrink-0 overflow-hidden">
               <div className="w-full rounded-full" style={{ height: '60%', background: accentColor }} />
            </div>
         )}
         
         {/* Main content */}
         <div className={`flex items-center gap-1.5 min-w-0 ${variant === 'card' ? 'flex-1' : ''}`}>
            {icon && <span className="text-xs shrink-0">{icon}</span>}
            <div className="min-w-0">
               <div className="flex items-center gap-1.5">
                  <span className="text-gray-200 text-xs truncate">{label}</span>
                  {badge && (
                     <StatusBadge preset={badge as BadgePreset}>{badge}</StatusBadge>
                  )}
               </div>
               {sub && (
                  <div className="text-[10px] text-gray-500 font-mono mt-0.5">{sub}</div>
               )}
            </div>
         </div>
         
         {/* Right value */}
         {value !== undefined && (
            <div className={`shrink-0 ml-2 text-xs font-mono ${valueColor}`}>
               {value}
            </div>
         )}
      </Tag>
   );
};

// ── ItemList ─────────────────────────────────────────────────────────────────

export interface ItemListProps {
   items: ItemRowProps[];
   /** Max rows before showing overflow button */
   maxVisible?: number;
   /** Text for the overflow button, receives hidden count */
   overflowLabel?: (hidden: number) => string;
   onOverflowClick?: (e: React.MouseEvent) => void;
   emptyMessage?: string;
}

const ItemList: React.FC<ItemListProps> = ({
      items,
      maxVisible = Infinity,
      overflowLabel = (n) => `+${n} more — click to inspect`,
      onOverflowClick,
      emptyMessage = 'None.',
   }) => {
   if (items.length === 0) {
      return <p className="text-gray-600 text-xs px-1">{emptyMessage}</p>;
   }
   
   const visible = items.slice(0, maxVisible);
   const hidden = items.length - visible.length;
   
   return (
      <div className="space-y-1">
         {visible.map((item) => (
            <ItemRow key={item.id} {...item} />
         ))}
         {hidden > 0 && (
            <button
               onClick={onOverflowClick ? (e) => { e.stopPropagation(); onOverflowClick(e); } : undefined}
               className="text-xs text-gray-600 hover:text-gray-300 px-2.5 py-1 w-full text-left transition-colors"
            >
               {overflowLabel(hidden)}
            </button>
         )}
      </div>
   );
};

export default ItemList;
